import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type RecordingState = "off" | "consent_pending" | "recording" | "declined";

interface RoomRow {
  id: string;
  recording_state: RecordingState;
  recording_mode?: string | null;
}

/**
 * Tracks the recording state of the active video room for a session and
 * exposes helpers for both clients (granting consent) and coaches
 * (requesting / cancelling / starting recording).
 */
export function useRecordingConsent(sessionId: string | null | undefined) {
  const [room, setRoom] = useState<RoomRow | null>(null);
  const [hasGranted, setHasGranted] = useState(false);
  const [grantedUserIds, setGrantedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Initial fetch + realtime subscription on the room and consents.
  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    let consentsChannel: ReturnType<typeof supabase.channel> | null = null;

    (async () => {
      const { data: r } = await supabase
        .from("session_video_rooms")
        .select("id, recording_state, recording_mode")
        .eq("session_id", sessionId)
        .is("ended_at", null)
        .maybeSingle();
      if (!active) return;
      setRoom((r as RoomRow) ?? null);

      if (r?.id) {
        const { data: cs } = await supabase
          .from("session_recording_consents")
          .select("user_id")
          .eq("video_room_id", r.id);
        if (!active) return;
        const ids = (cs ?? []).map((c) => c.user_id as string);
        setGrantedUserIds(ids);
        const { data: auth } = await supabase.auth.getUser();
        if (auth.user) setHasGranted(ids.includes(auth.user.id));

        consentsChannel = supabase
          .channel(`recording-consents-${r.id}-${Math.random().toString(36).slice(2)}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "session_recording_consents",
              filter: `video_room_id=eq.${r.id}`,
            },
            async () => {
              const { data: cs2 } = await supabase
                .from("session_recording_consents")
                .select("user_id")
                .eq("video_room_id", r.id);
              const ids2 = (cs2 ?? []).map((c) => c.user_id as string);
              setGrantedUserIds(ids2);
              const { data: a } = await supabase.auth.getUser();
              if (a.user) setHasGranted(ids2.includes(a.user.id));
            },
          )
          .subscribe();
      }
    })();

    const roomChannel = supabase
      .channel(`recording-room-${sessionId}-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "session_video_rooms",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const row = payload.new as RoomRow | undefined;
          if (payload.eventType === "DELETE") {
            setRoom(null);
          } else if (row) {
            setRoom({
              id: row.id,
              recording_state: row.recording_state,
              recording_mode: row.recording_mode ?? null,
            });
          }
        },
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(roomChannel);
      if (consentsChannel) supabase.removeChannel(consentsChannel);
    };
  }, [sessionId]);

  const grantConsent = useCallback(async () => {
    if (!room) return { ok: false, error: "No active room" };
    setLoading(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return { ok: false, error: "Not signed in" };
      const { error } = await supabase
        .from("session_recording_consents")
        .insert({ video_room_id: room.id, user_id: auth.user.id });
      if (error && !/duplicate/i.test(error.message)) {
        return { ok: false, error: error.message };
      }
      setHasGranted(true);
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, [room]);

  const requestConsent = useCallback(async () => {
    if (!sessionId) return { ok: false };
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "request-recording-consent",
        { body: { sessionId, action: "request" } },
      );
      if (error || data?.error) return { ok: false, error: error?.message || data?.error };
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const cancelConsentRequest = useCallback(async () => {
    if (!sessionId) return { ok: false };
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "request-recording-consent",
        { body: { sessionId, action: "cancel" } },
      );
      if (error || data?.error) return { ok: false, error: error?.message || data?.error };
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const startRecording = useCallback(async (mode: "audio" | "video" = "video") => {
    if (!sessionId) return { ok: false };
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "start-session-recording",
        { body: { sessionId, mode } },
      );
      if (error || data?.error) {
        // A non-2xx reply surfaces as a generic message, so read the real
        // reason (including the provider detail) from the response body.
        let payload: { error?: string; missing?: string[]; details?: string } | null =
          (data as { error?: string; missing?: string[]; details?: string }) ?? null;
        const context = (error as { context?: Response } | null)?.context;
        if (error && context && typeof context.text === "function") {
          try {
            payload = JSON.parse(await context.text());
          } catch {
            /* keep the generic message */
          }
        }
        if (payload?.details) {
          console.error("start-session-recording failed:", payload.details);
        }
        return {
          ok: false,
          error: payload?.error || error?.message,
          missing: payload?.missing,
        };
      }
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const stopRecording = useCallback(async () => {
    if (!sessionId) return { ok: false };
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("control-recording", {
        body: { sessionId, action: "stop" },
      });
      if (error || data?.error) {
        return { ok: false, error: error?.message || data?.error };
      }
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  return {
    recordingState: room?.recording_state ?? "off",
    recordingMode: (room?.recording_mode as "audio" | "video" | null) ?? null,
    grantedCount: grantedUserIds.length,
    grantedUserIds,
    hasGranted,
    loading,
    grantConsent,
    requestConsent,
    cancelConsentRequest,
    startRecording,
    stopRecording,
  };
}
